import React, { ReactNode } from 'react';

import { DoubleTextNodeClamp } from '@components/common';
import ChangedField from '@components/common/ChangedField';

import { TChangedAssetProfile } from '@interfaces/assets/assetHistory';

import { attributesFields, locationFields } from './constants';
import { EProfileFields } from '@components/routes/AdminPanel/AssetHistory/AssetHistoryProfile/constants';

import { getField } from '../util';

import styles from './Summary.module.scss';

//add export for testing purposes
export function getAddress(profile: TChangedAssetProfile) {
	const address = [
		profile?.adPropertyNoName,
		profile?.adStreetName,
		profile?.adLocality,
		profile?.adTownCity,
		profile?.adPostCode,
	].filter((item) => !!item);

	const joinAddress = (previousAddress: ReactNode, updatedAddress: ReactNode) =>
		`${previousAddress ? `${previousAddress}, ` : ''}${updatedAddress ? updatedAddress : ''}`;

	return address.length
		? address.reduce((acc, next) => {
			if (!next) return acc;

			const { current, previous, isChanged } = next;

			return {
				current: joinAddress(acc?.current, current),
				previous: joinAddress(acc?.previous, previous),
				isChanged: acc?.isChanged || isChanged,
			};
		})
		: null;
}

const Person = ({ profile }: Record<string, any>) => {
	const field = getField(profile, EProfileFields.contactPerson);

	if (!field) return null;

	const { firstName, lastName, email } = field;

	const getName = (firstName?: string, lastName?: string) => {
		if (!firstName || !lastName) return null;

		return `${firstName} ${lastName}`;
	};

	const currentName = getName(firstName?.current, lastName?.current);
	const previousName = getName(firstName?.previous, lastName?.previous);

	return (
		<>
			{firstName && lastName && (
				<ChangedField
					current={currentName}
					fieldTitle='Contact Person'
					isChanged={firstName.isChanged || lastName.isChanged}
					previous={previousName}
					testId='firstName'
				/>
			)}
			{email &&
				<ChangedField
					{...email}
					fieldTitle='Email'
					testId='email'
				/>}
		</>
	);
};

const AttributesSummary = ({ profile }: Record<string, any>) => {
	return (
		<>
			<Person profile={profile} />
			{attributesFields.map((item: Record<string, any>, key) => {
				const field = getField(profile, item.fieldName);

				return field &&
					<ChangedField
						key={`${item.fieldName}_${key}`}
						current={field.current}
						fieldTitle={item.fieldTitle}
						isChanged={field.isChanged}
						previous={field.previous}
						testId={field.testId}
					/>;
			})}
			{profile?.assetOwnerNotes && (
				<>
					<div>Notes</div>
					<DoubleTextNodeClamp {...profile.assetOwnerNotes} />
				</>
			)}
		</>
	);
};

const LocationSummary = ({ profile }: { profile: TChangedAssetProfile }) => {
	const address = getAddress(profile);

	return (
		<>
			{address && (
				<>
					<div>Address</div>
					<DoubleTextNodeClamp {...address} />
				</>
			)}
			{locationFields.map((item: Record<string, any>, key) => {
				const field = getField(profile, item.fieldName);

				return field &&
					<ChangedField
						key={`${item.fieldName}_${key}`}
						current={field.current}
						fieldTitle={item.fieldTitle}
						isChanged={field.isChanged}
						previous={field.previous}
						testId={field.testId}
					/>;
			})}
			{profile?.locationDescription && (
				<>
					<div>
						Location
						<br />
						description
					</div>
					<DoubleTextNodeClamp {...profile.locationDescription} />
				</>
			)}
		</>
	);
};

const Summary = ({ profile }: { profile: TChangedAssetProfile }) => {
	return (
		<div className={styles.summaryContainer}>
			<div className={styles.listContainer}>
				<div>
					<div className={styles.list}>
						<LocationSummary profile={profile} />
					</div>
				</div>
				<div>
					<div className={styles.list}>
						<AttributesSummary profile={profile} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default Summary;
